/**
 * identity-service client (dao-suite) — the suite-login front-door wiring.
 *
 * Story: uid-001-suite-login-frontdoor (capstone — activates the member-side
 * dark foundation, unblocks frontend-001-9f-c).
 *
 * This module owns the credentialed XHRs the member suite makes to the
 * identity-service for the hosted-login-front-door flow:
 *
 *  1. {@link buildLoginFrontdoorUrl} — the URL the suite redirects an
 *     unauthenticated member to. The hosted login UI authenticates the user
 *     (email+password, TOTP) against identity-service, then redirects back to
 *     `<return_origin>?code=<one-time-code>`.
 *  2. {@link exchangeCodeForJwt} — on the `?code=` return, POST `/token/exchange`
 *     `{code}` (credentialed) → `{jwt}`. The httpOnly `hwdao_refresh` cookie is set
 *     by identity-service on this same response (Domain=.helloworlddao.com) — JS
 *     never sees it.
 *  3. {@link refreshIdentityJwt} — silent refresh: POST `/token/refresh` with
 *     `credentials: 'include'` and NO body (the httpOnly cookie carries the refresh
 *     token) → fresh `{jwt}` + rotated cookie.
 *  4. {@link logoutIdentitySession} — POST `/logout` (credentialed) to clear the
 *     refresh cookie + revoke the token family.
 *
 * **Backward-compatible.** None of this forces existing cookie sessions to log
 * out. The suite keeps working on the legacy Think-Tank/oracle-bridge cookie path;
 * it merely *also* obtains + presents an identity-service JWT once the front-door
 * flow runs. The cookie retirement is a SEPARATE later story.
 *
 * The identity-service base + the hosted-login-front-door base are env-driven
 * (`VITE_IDENTITY_SERVICE_URL`, `VITE_LOGIN_FRONTDOOR_URL`) so staging/prod differ
 * by config, not code.
 */

import { boundedRefresh, type RefreshOutcome } from '@hello-world-dao/auth';

/** Default identity-service base (staging) when the env var is unset. */
const DEFAULT_IDENTITY_SERVICE_URL = 'https://identity.staging.helloworlddao.com';

/** Default hosted-login-front-door base (staging) when the env var is unset. */
const DEFAULT_LOGIN_FRONTDOOR_URL = 'https://register.staging.helloworlddao.com';

/** The query parameter the hosted login UI reads for the cross-domain handoff. */
export const RETURN_ORIGIN_PARAM = 'return_origin';

/** The query parameter the front-door redirect carries the one-time code in. */
export const CODE_PARAM = 'code';

/**
 * sessionStorage key holding the path+search the member requested before being
 * bounced to the login front-door (bl-714 deep-link restore). sessionStorage (not
 * a module variable) so it survives the full-page cross-origin redirect to the
 * hosted login UI and back; scoped to the tab so it does not leak across tabs.
 */
const DEEP_LINK_KEY = 'hwdao_login_return_path';

/**
 * Stash the current in-suite path+search so the member can be returned to it after
 * the front-door `?code=`→exchange completes (bl-714). Called right before the
 * redirect to the hosted login UI (which lands the member back at the bare suite
 * origin, losing the originally-requested deep path otherwise).
 *
 * The `?code=` return param is NOT included (the handler strips it); the bare
 * root path `/` is not worth stashing (it is the default landing anyway).
 */
export function stashDeepLinkReturnPath(): void {
  try {
    const path = `${window.location.pathname}${window.location.search}`;
    const clean = stripCodeParam(path);
    if (clean && clean !== '/') {
      window.sessionStorage.setItem(DEEP_LINK_KEY, clean);
    }
  } catch {
    // sessionStorage unavailable (private mode / disabled) — deep-link restore is
    // best-effort; the member still lands authed at the suite root.
  }
}

/**
 * Read + clear the stashed deep-link return path (bl-714). Returns the path to
 * navigate to after a successful exchange, or null when none was stashed.
 */
export function takeDeepLinkReturnPath(): string | null {
  try {
    const path = window.sessionStorage.getItem(DEEP_LINK_KEY);
    if (path) window.sessionStorage.removeItem(DEEP_LINK_KEY);
    return path && path !== '/' ? path : null;
  } catch {
    return null;
  }
}

/** Strip a `?code=` (or `&code=`) param from a path+search string. */
function stripCodeParam(pathAndSearch: string): string {
  const qIndex = pathAndSearch.indexOf('?');
  if (qIndex === -1) return pathAndSearch;
  const path = pathAndSearch.slice(0, qIndex);
  const params = new URLSearchParams(pathAndSearch.slice(qIndex + 1));
  params.delete(CODE_PARAM);
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

/** identity-service base URL, trailing slash stripped. */
export function getIdentityServiceUrl(): string {
  const raw = import.meta.env.VITE_IDENTITY_SERVICE_URL || DEFAULT_IDENTITY_SERVICE_URL;
  return raw.replace(/\/+$/, '');
}

/** Hosted-login-front-door base URL, trailing slash stripped. */
export function getLoginFrontdoorUrl(): string {
  const raw = import.meta.env.VITE_LOGIN_FRONTDOOR_URL || DEFAULT_LOGIN_FRONTDOOR_URL;
  return raw.replace(/\/+$/, '');
}

/**
 * This suite's own served origin — the `return_origin` the front-door redirects
 * back to after authentication. Using `window.location.origin` (rather than a
 * hard-coded env value) guarantees the origin is the exact host serving the page,
 * which is also what identity-service's `ALLOWED_RETURN_ORIGINS` + credentialed
 * CORS allowlist are keyed on.
 */
export function getSuiteOrigin(): string {
  return window.location.origin;
}

/**
 * Build the hosted-login-front-door URL to redirect an unauthenticated member to.
 * The front-door authenticates the user, then redirects back to
 * `<return_origin>?code=<code>`.
 *
 * @returns e.g. `https://register.staging.helloworlddao.com/login?return_origin=https%3A%2F%2Fportal.staging.helloworlddao.com`
 */
export function buildLoginFrontdoorUrl(): string {
  // bl-714: stash the originally-requested deep path before the cross-origin
  // redirect to the hosted login UI. The front-door bounces the member back to the
  // bare suite origin (`<origin>?code=`), so without this the deep link is lost;
  // the pre-mount code→JWT bootstrap (identity-code-bootstrap) restores it after
  // the `?code=`→exchange.
  stashDeepLinkReturnPath();
  const url = new URL(`${getLoginFrontdoorUrl()}/login`);
  url.searchParams.set(RETURN_ORIGIN_PARAM, getSuiteOrigin());
  return url.toString();
}

/**
 * Build the hosted-front-door `/forgot-password` URL (bl-777). The password-reset
 * surface is unbuilt in this suite — it lives on the auth front-door alongside
 * /login (registration-suite), because identity-service emails the reset link as
 * `{FRONTEND_URL}/reset-password?token=`, which lands on the front-door origin.
 * The portal's empty `/forgot-password` route redirects here, mirroring how
 * `/login` redirects to {@link buildLoginFrontdoorUrl}.
 *
 * NO `return_origin`: the reset flow terminates on the front-door (the
 * reset-success page links to /login), so there is no cross-domain `?code=`
 * handoff to wire back to the suite.
 *
 * @returns e.g. `https://register.staging.helloworlddao.com/forgot-password`
 */
export function buildForgotPasswordFrontdoorUrl(): string {
  return `${getLoginFrontdoorUrl()}/forgot-password`;
}

/** Shape of the identity-service `/token/exchange` + `/token/refresh` success body. */
interface JwtResponse {
  jwt: string;
}

/**
 * Exchange a one-time front-door code for an identity-service access JWT.
 *
 * POSTs `/token/exchange {code}` CREDENTIALED (so identity-service can set the
 * httpOnly `hwdao_refresh` cookie on the response). Returns the JWT on success, or
 * `null` on any failure (invalid/used/expired code, network error) — callers fall
 * back to the existing cookie session rather than hard-breaking.
 *
 * ## PKCE code_verifier (bl-1107-a — reverse FOS→DAO cross-domain login)
 *
 * The optional `codeVerifier` supports the reverse "Switch to DAO" crossing. A
 * code minted by identity-service `/cross-domain/authorize` is PKCE/challenge-bound
 * and can ONLY be redeemed with its matching `code_verifier`; `token_exchange`'s
 * gate is *challenge present ⇒ verifier REQUIRED* (absent/mismatch → uniform 401).
 * The verifier travels ONLY in the receiver's `#v=` URL fragment (never a server
 * request on navigation) and is threaded here by the
 * `/auth/cross-domain-login` receiver.
 *
 * **Additive + backward-compatible.** When no verifier is passed the body is the
 * unchanged `{ code }` — the legacy NULL-challenge front-door exchange
 * (`identity-code-bootstrap` `?code=` path) is byte-for-byte identical. The
 * `code_verifier` key is included ONLY when a non-empty verifier is supplied, so a
 * front-door code never carries a spurious `code_verifier: null` that a stricter
 * server might reject.
 */
export async function exchangeCodeForJwt(
  code: string,
  codeVerifier?: string,
): Promise<string | null> {
  try {
    const body: { code: string; code_verifier?: string } = { code };
    // Include `code_verifier` ONLY when a non-empty verifier is present (a
    // cross-domain PKCE crossing). A front-door code has none to send, and adding
    // a `null`/empty key would diverge the legacy body shape.
    if (codeVerifier) body.code_verifier = codeVerifier;
    const res = await fetch(`${getIdentityServiceUrl()}/token/exchange`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as JwtResponse | null;
    return data?.jwt && data.jwt.length > 0 ? data.jwt : null;
  } catch {
    return null;
  }
}

/**
 * Silently refresh the identity-service access JWT using the httpOnly refresh
 * cookie.
 *
 * POSTs `/token/refresh` with `credentials: 'include'` and NO body — the cookie
 * carries the refresh token; identity-service rotates it (fresh cookie) and mints
 * a new access JWT. Returns the fresh JWT, or `null` when refresh fails (expired /
 * revoked / reused / no cookie) so the caller can clear the JWT and redirect to
 * login.
 */
export async function refreshIdentityJwt(): Promise<string | null> {
  try {
    const res = await fetch(`${getIdentityServiceUrl()}/token/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const data = (await res.json()) as JwtResponse | null;
    return data?.jwt && data.jwt.length > 0 ? data.jwt : null;
  } catch {
    return null;
  }
}

/**
 * Bounded, reuse-safe variant of {@link refreshIdentityJwt} for the pre-mount
 * session-rehydrate bootstrap (bl-977).
 *
 * Unlike `refreshIdentityJwt` (which collapses every failure to `null`, so a
 * transient blip is indistinguishable from a genuine logout and bounces a
 * valid-cookie member to the sign-in shell), this returns a discriminated
 * {@link RefreshOutcome}:
 *   - `authenticated` — a 200 with a well-formed JWT.
 *   - `unauthenticated` — a definitive HTTP 401 (expired / revoked / reused / no
 *     cookie). ONLY this outcome should paint the login shell.
 *   - `transient-error` — timeout, 5xx/429, malformed 200, or a connection throw
 *     whose retries were exhausted. The caller MUST hold a loader (never login).
 *
 * The attempt is bounded by an `AbortController` timeout so a hung refresh can't
 * stall the load. bl-1248-1: a genuine 5xx is now retried AT MOST ONCE inside a
 * deadline ≤ the server grace window (safe because a post-500 either handed the
 * successor back as a Set-Cookie or lands in the grace window — see boundedRefresh);
 * a timeout / malformed-200 / 401 / 429 is never retried, and a connection-level
 * throw retries on its own bl-789-safe budget. Retries are strictly sequential
 * inside the caller's single-flight.
 *
 * bl-985: re-pointed from the suite-local `backoffRefresh` pilot copy to the shared
 * `@hello-world-dao/auth` `boundedRefresh` export (auth@3.1.0) — de-duplicated so
 * dao-suite and the sibling suites all share one canonical, unit-tested primitive.
 * Behavior is identical (same signature, same defaults).
 */
export async function refreshIdentityJwtBounded(): Promise<RefreshOutcome<string>> {
  return boundedRefresh<string>(
    (signal) =>
      fetch(`${getIdentityServiceUrl()}/token/refresh`, {
        method: 'POST',
        credentials: 'include',
        signal,
      }),
    async (res) => {
      const data = (await res.json()) as JwtResponse | null;
      return data?.jwt && data.jwt.length > 0 ? data.jwt : null;
    },
  );
}

/**
 * Revoke the identity-service refresh-token family + clear the httpOnly cookie.
 * Best-effort: a failure does not block the rest of the logout flow.
 */
export async function logoutIdentitySession(): Promise<void> {
  try {
    await fetch(`${getIdentityServiceUrl()}/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Best-effort — the caller proceeds with the local clear + redirect.
  }
}
